import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { FiUser, FiMail, FiMapPin, FiEdit2, FiPhone, FiEye } from 'react-icons/fi';
import EditProfileModal from '../Components/EditProfileModal';
import { updateUserInfo } from '../store/userSlice';
import { toast } from 'react-hot-toast';
import axiosInstance from '../api/axiosInstance';

const ProfileInfo = () => {
  const { currentUser } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('personal'); // 'personal', 'editAddress', 'newAddress'
  const [selectedAddress, setSelectedAddress] = useState(null);
  const dispatch = useDispatch();

  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // If user is not logged in, don't render the profile content
  if (!currentUser) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen bg-cream flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-display font-normal tracking-tight text-gray-900">Please log in to view your profile</h2>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const handleEditPersonal = () => {
    setModalMode('personal');
    setIsModalOpen(true);
  };

  const handleAddAddress = () => {
    setModalMode('newAddress');
    setSelectedAddress(null);
    setIsModalOpen(true);
  };

  const handleEditAddress = (address) => {
    setModalMode('editAddress');
    setSelectedAddress(address);
    setIsModalOpen(true);
  };

  const handleSaveChanges = async (formData) => {
    try {
      if (modalMode === 'personal') {
        const { data } = await axiosInstance.put('/users/updateMe', {
          name: formData.name,
          email: formData.email,
        });
        dispatch(updateUserInfo(data.data));
      } else {
        const payload = {
          alias: (formData.alias || 'Home').trim(),
          details: (formData.details || '').trim(),
          city: (formData.city || '').trim(),
          phone: (formData.phone || '').trim(),
          postalCode: (formData.postalCode || '').trim() || '00000',
        };
        // No address-update endpoint exists, so an edit = remove old + add new.
        if (modalMode === 'editAddress' && selectedAddress?._id) {
          await axiosInstance.delete(`/addresses/${selectedAddress._id}`);
        }
        const { data } = await axiosInstance.post('/addresses', payload);
        dispatch(updateUserInfo({ addresses: data.data }));
        toast.success('Address saved.');
      }
      setIsModalOpen(false);
    } catch (error) {
      toast.error(
        error.response?.data?.errors?.[0]?.msg
        || error.response?.data?.message
        || 'Could not save. Please check the fields and try again.',
      );
    }
  };

  const handleRemoveAddress = async (addressId) => {
    try {
      const { data } = await axiosInstance.delete(`/addresses/${addressId}`);
      dispatch(updateUserInfo({ addresses: data.data }));
      toast.success('Address removed.');
    } catch (error) {
      toast.error('Could not remove the address.');
    }
  };

  return (
    <>
      <Navbar />
      <div className="pt-24 min-h-screen bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl border border-sand shadow-card overflow-hidden">
            {/* Profile Header */}
            <div className="bg-ink text-cream p-6">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 rounded-full bg-ink-soft flex items-center justify-center">
                  {currentUser.profileImg ? (
                    <img 
                      src={currentUser.profileImg} 
                      alt={currentUser.name} 
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <FiUser className="w-10 h-10" />
                  )}
                </div>
                <div>
                  <h1 className="text-2xl font-display font-normal tracking-tight">{currentUser.name}</h1>
                  <p className="text-cream/70">{currentUser.email}</p>
                  {currentUser.phone && (
                    <p className="text-cream/70">{currentUser.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Navigation */}
            <div className="border-b border-sand">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('personal')}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === 'personal'
                      ? 'border-b-2 border-brand-500 text-brand-700'
                      : 'text-clay hover:text-gray-700'
                  }`}
                >
                  Personal Information
                </button>
                <button
                  onClick={() => setActiveTab('addresses')}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === 'addresses'
                      ? 'border-b-2 border-brand-500 text-brand-700'
                      : 'text-clay hover:text-gray-700'
                  }`}
                >
                  Addresses
                </button>
              </nav>
            </div>

            {/* Profile Content */}
            <div className="p-6">
              {activeTab === 'personal' ? (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-display font-normal tracking-tight text-gray-900 mb-4">Personal Information</h2>
                    <div className="bg-cream rounded-xl border border-sand p-4 space-y-4">
                      <div className="flex items-center">
                        <FiUser className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Full Name</p>
                          <p className="font-medium">{currentUser.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FiMail className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Email Address</p>
                          <p className="font-medium">{currentUser.email}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleEditPersonal}
                      className="mt-4 flex items-center text-sm text-brand-600 hover:text-brand-700"
                    >
                      <FiEdit2 className="w-4 h-4 mr-1" />
                      Edit Personal Information
                    </button>
                  </div>

                  {/* Prescription */}
                  <div>
                    <h2 className="text-lg font-display font-normal tracking-tight text-gray-900 mb-4">My Prescription</h2>
                    {currentUser.prescription && (currentUser.prescription.rightEye || currentUser.prescription.leftEye) ? (
                      <div className="bg-cream rounded-xl border border-sand p-4 space-y-4">
                        {[
                          { label: 'Right eye (OD)', eye: currentUser.prescription.rightEye },
                          { label: 'Left eye (OS)', eye: currentUser.prescription.leftEye },
                        ].map(({ label, eye }) => (
                          <div className="flex items-center" key={label}>
                            <FiEye className="w-5 h-5 text-brand-500 mr-3 flex-none" />
                            <div>
                              <p className="text-sm text-gray-500">{label}</p>
                              <p className="font-medium tabular-nums">
                                SPH {eye?.sph ?? '—'} &nbsp;·&nbsp; CYL {eye?.cyl ?? '—'} &nbsp;·&nbsp; AXIS {eye?.axis ?? '—'}
                              </p>
                            </div>
                          </div>
                        ))}
                        <p className="text-xs text-clay">
                          {currentUser.prescription.method === 'upload' ? 'Read from an uploaded prescription' : 'Entered manually'}
                          {' · '}
                          <Link to="/eye-power-finder" className="text-brand-600 hover:text-brand-700">Update</Link>
                        </p>
                      </div>
                    ) : (
                      <div className="bg-cream rounded-xl border border-sand p-4 text-sm text-clay">
                        No prescription saved yet. Add one with the{' '}
                        <Link to="/eye-power-finder" className="text-brand-600 hover:text-brand-700">Eye Power Finder</Link>.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-display font-normal tracking-tight text-gray-900">Saved Addresses</h2>
                      <button
                        onClick={handleAddAddress}
                        className="text-sm text-brand-600 hover:text-brand-700 flex items-center"
                      >
                        <FiEdit2 className="w-4 h-4 mr-1" />
                        Add New Address
                      </button>
                    </div>
                    <div className="space-y-4">
                      {(currentUser.addresses || []).length === 0 && (
                        <div className="bg-cream rounded-xl border border-sand p-6 text-center text-clay">
                          No addresses saved yet.
                        </div>
                      )}
                      {(currentUser.addresses || []).map((address) => (
                        <div
                          key={address._id || address.id}
                          className="bg-cream rounded-xl border border-sand p-4 relative"
                        >
                          <div className="flex items-start">
                            <FiMapPin className="w-5 h-5 text-brand-500 mr-3 mt-1 flex-none" />
                            <div className="min-w-0">
                              <p className="font-medium text-ink">{address.alias || 'Address'}</p>
                              {address.details && (
                                <p className="text-gray-600 mt-0.5">{address.details}</p>
                              )}
                              <p className="text-gray-600">{address.city}</p>
                              <p className="text-gray-600">Phone: {address.phone}</p>
                              {address.lat != null && address.lng != null && (
                                <a
                                  className="inline-block mt-1 text-sm text-brand-600 hover:text-brand-700"
                                  href={`https://maps.google.com/?q=${address.lat},${address.lng}`}
                                  target="_blank" rel="noreferrer"
                                >
                                  View on map →
                                </a>
                              )}
                              <div className="mt-2 space-x-4">
                                <button
                                  onClick={() => handleEditAddress(address)}
                                  className="text-sm text-brand-600 hover:text-brand-700"
                                >
                                  Edit
                                </button>
                                <span className="text-sand">|</span>
                                <button
                                  onClick={() => handleRemoveAddress(address._id || address.id)}
                                  className="text-sm text-red-600 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <EditProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        currentData={modalMode === 'personal' ? currentUser : selectedAddress}
        onSave={handleSaveChanges}
      />
    </>
  );
};

export default ProfileInfo; 